import React, { useState } from 'react';
import { DADA, TornLabel, DadaStyles } from './dada';
import { useTranslation } from '../i18n';

// Lokale, kleinere helpers in dezelfde dadaïstische taal als het dashboard.
function ChaosTitle({ children, variant = 'dark', rotate = '-rotate-1', className = '' }) {
  const palette = {
    green: 'bg-[#3f7f65] text-white',
    purple: 'bg-[#713d8d] text-white',
    dark: 'bg-[#151827] text-white',
    cream: 'bg-[#fffdf8] text-[#151827]',
  };
  return (
    <span
      className={`inline-block ${rotate} border-[3px] border-[#151827] ${palette[variant]} px-4 py-1.5 text-base sm:text-lg font-black uppercase tracking-tight shadow-[6px_6px_0_#151827] ${className}`}
    >
      {children}
    </span>
  );
}

function StepCard({ number, title, body, accent = 'green' }) {
  const accentColor = accent === 'purple' ? DADA.purple : DADA.green;
  return (
    <div
      className="relative flex h-full flex-col rounded-xl border-4 border-[#151827] bg-white p-5 shadow-[6px_6px_0_#151827]"
    >
      <div
        className="mb-3 inline-block text-3xl font-black leading-none"
        style={{ color: accentColor }}
      >
        {number}
      </div>
      <div className="text-base font-black text-[#151827]">{title}</div>
      <p className="mt-2 text-sm leading-relaxed text-[#2a2f40]">{body}</p>
    </div>
  );
}

function PillarCard({ icon, title, body, color }) {
  return (
    <div
      className="rounded-xl border-4 border-[#151827] p-5 shadow-[6px_6px_0_#151827]"
      style={{ backgroundColor: color, color: '#ffffff' }}
    >
      <div className="text-2xl">{icon}</div>
      <div className="mt-2 text-base font-black uppercase tracking-tight">{title}</div>
      <p className="mt-2 text-sm leading-relaxed opacity-95">{body}</p>
    </div>
  );
}

function TimelineMarker({ marker, label, body }) {
  return (
    <div className="flex-1 rounded-xl border-4 border-[#151827] bg-white p-5 shadow-[6px_6px_0_#151827]">
      <div className="text-2xl font-black text-[#713d8d]">{marker}</div>
      <div className="mt-1 text-xs font-black uppercase tracking-widest text-[#151827]">{label}</div>
      <p className="mt-2 text-sm leading-relaxed text-[#2a2f40]">{body}</p>
    </div>
  );
}

/**
 * MatchingInfographic
 * Uitleg van het matching-proces in dashboard-stijl voor bedrijf en kandidaat.
 * Inklapbaar zodat het dashboard rustig blijft.
 */
export default function MatchingInfographic({ defaultOpen = false, audience = 'company' }) {
  const [open, setOpen] = useState(defaultOpen);
  const { t } = useTranslation();

  const isCompany = audience === 'company';

  return (
    <section>
      <DadaStyles />

      {/* TOGGLE HEADER (zelfde stijl als andere dashboardwidgets) */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="group flex w-full items-center justify-between rounded-xl border-4 border-[#151827] bg-white/95 px-6 py-4 transition-all shadow-[10px_10px_0_#151827]"
      >
        <div className="flex items-center gap-3">
          <span className="rounded-lg border-2 border-[#151827] bg-[#fffdf8] px-2 py-1 text-2xl">🧭</span>
          <div className="text-left">
            <div className="text-lg font-black text-[#151827]">{t('matching_infographic.toggle_title')}</div>
            <div className="text-xs text-gray-500">{t('matching_infographic.toggle_subtitle')}</div>
          </div>
        </div>
        <span className={`text-[#151827]/70 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>

      {open && (
        <div className="mt-3 space-y-8 rounded-xl border-4 border-[#151827] bg-white/95 p-6 shadow-[10px_10px_0_#151827] sm:p-8">

          {/* INTRO */}
          <div className="flex flex-wrap items-center gap-3">
            <ChaosTitle variant="dark" rotate="-rotate-2">{t('matching_infographic.intro_title')}</ChaosTitle>
            <TornLabel className="rotate-1" bgColor={DADA.green} textColor="#ffffff">{t('matching_infographic.labels.no_gut_feeling')}</TornLabel>
            <TornLabel className="-rotate-1" bgColor={DADA.purple} textColor="#ffffff">{t('matching_infographic.labels.proof_100_hours')}</TornLabel>
          </div>
          <p className="max-w-3xl text-base leading-relaxed text-[#2a2f40]">
            {t('matching_infographic.intro_body_prefix')}
            <span className="font-black"> {t('matching_infographic.intro_body_pillars')}</span>{t('matching_infographic.intro_body_mid')}
            <span className="font-black">{t('matching_infographic.intro_body_proof')}</span>.
            {t('matching_infographic.intro_body_suffix')}
          </p>

          {/* HET PROCES IN 6 STAPPEN */}
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <ChaosTitle variant="green" rotate="-rotate-1">{t('matching_infographic.process_title')}</ChaosTitle>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StepCard
                number="01"
                title={isCompany ? t('matching_infographic.steps.company_1_title') : t('matching_infographic.steps.candidate_1_title')}
                body={isCompany
                  ? t('matching_infographic.steps.company_1_body')
                  : t('matching_infographic.steps.candidate_1_body')}
                accent="green"
              />
              <StepCard
                number="02"
                title={t('matching_infographic.steps.step_2_title')}
                body={t('matching_infographic.steps.step_2_body')}
                accent="purple"
              />
              <StepCard
                number="03"
                title={t('matching_infographic.steps.step_3_title')}
                body={t('matching_infographic.steps.step_3_body')}
                accent="green"
              />
              <StepCard
                number="04"
                title={t('matching_infographic.steps.step_4_title')}
                body={t('matching_infographic.steps.step_4_body')}
                accent="purple"
              />
              <StepCard
                number="05"
                title={t('matching_infographic.steps.step_5_title')}
                body={isCompany
                  ? t('matching_infographic.steps.company_5_body')
                  : t('matching_infographic.steps.candidate_5_body')}
                accent="green"
              />
              <StepCard
                number="06"
                title={t('matching_infographic.steps.step_6_title')}
                body={t('matching_infographic.steps.step_6_body')}
                accent="purple"
              />
            </div>
          </div>

          {/* DE 4 PIJLERS */}
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <ChaosTitle variant="purple" rotate="rotate-1">{t('matching_infographic.pillars_title')}</ChaosTitle>
              <TornLabel className="-rotate-2" bgColor="#fffdf8">{t('matching_infographic.pillars_badge')}</TornLabel>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <PillarCard
                icon="🧬"
                title={t('matching_infographic.pillars.identity.title')}
                body={t('matching_infographic.pillars.identity.body')}
                color={DADA.ink}
              />
              <PillarCard
                icon="🎯"
                title={t('matching_infographic.pillars.ambition.title')}
                body={t('matching_infographic.pillars.ambition.body')}
                color={DADA.purple}
              />
              <PillarCard
                icon="🛠️"
                title={t('matching_infographic.pillars.skills.title')}
                body={t('matching_infographic.pillars.skills.body')}
                color={DADA.green}
              />
              <PillarCard
                icon="⚖️"
                title={t('matching_infographic.pillars.conditions.title')}
                body={t('matching_infographic.pillars.conditions.body')}
                color="#1f3a30"
              />
            </div>
          </div>

          {/* WAAROM JOUW PROFIEL CRUCIAAL IS */}
          <div className="rounded-xl border-4 border-[#151827] bg-[#fffdf8] p-6 shadow-[6px_6px_0_#151827]">
            <div className="flex flex-wrap items-center gap-3">
              <ChaosTitle variant="dark" rotate="-rotate-2">{t('matching_infographic.visibility_title')}</ChaosTitle>
            </div>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-[#2a2f40]">
              {isCompany ? (
                <>
                  {t('matching_infographic.visibility.company_prefix')} <span className="font-black">{t('matching_infographic.visibility.company_accent_1')}</span>.
                  {t('matching_infographic.visibility.company_middle')}
                  <span className="font-black">{t('matching_infographic.visibility.company_accent_2')}</span> {t('matching_infographic.visibility.company_suffix')}
                </>
              ) : (
                <>
                  {t('matching_infographic.visibility.candidate_prefix')} <span className="font-black">{t('matching_infographic.visibility.candidate_accent_1')}</span>.
                  {t('matching_infographic.visibility.candidate_middle')}
                  <span className="font-black">{t('matching_infographic.visibility.candidate_accent_2')}</span> {t('matching_infographic.visibility.candidate_suffix')}
                </>
              )}
            </p>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-[#2a2f40]">
              {isCompany ? (
                <>{t('matching_infographic.visibility.company_short_prefix')} <span className="font-black">{t('matching_infographic.visibility.company_short_accent')}</span></>
              ) : (
                <>{t('matching_infographic.visibility.candidate_short_prefix')} <span className="font-black">{t('matching_infographic.visibility.candidate_short_accent')}</span></>
              )}
            </p>
          </div>

          {/* 100 UUR TIMELINE */}
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <ChaosTitle variant="green" rotate="-rotate-1">{t('matching_infographic.timeline_title')}</ChaosTitle>
            </div>
            <div className="flex flex-col gap-4 md:flex-row">
              <TimelineMarker
                marker="0 uur"
                label={t('matching_infographic.timeline.start_label')}
                body={t('matching_infographic.timeline.start_body')}
              />
              <TimelineMarker
                marker="50 uur"
                label={t('matching_infographic.timeline.mid_label')}
                body={t('matching_infographic.timeline.mid_body')}
              />
              <TimelineMarker
                marker="100 uur"
                label={t('matching_infographic.timeline.end_label')}
                body={t('matching_infographic.timeline.end_body')}
              />
            </div>
            <p className="mt-6 text-center text-xl font-black italic text-[#151827] sm:text-2xl">
              {t('matching_infographic.quote')}
            </p>
          </div>

          {/* VERVOLGSTAPPEN */}
          <div className="rounded-xl border-4 border-[#151827] bg-[#151827] p-6 text-white shadow-[6px_6px_0_#151827]">
            <div className="flex flex-wrap items-center gap-3">
              <ChaosTitle variant="cream" rotate="rotate-1">{t('matching_infographic.next_steps_title')}</ChaosTitle>
            </div>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/90">
              {t('matching_infographic.next_steps_prefix')} <span className="font-black">{t('matching_infographic.next_steps_opportunities')}</span>.
              {t('matching_infographic.next_steps_suffix')}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
